import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";
import { tokens } from "@/shared/design-system/tokens";

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
`;

/**
 * Badge visual del toast "PRÓXIMAMENTE".
 * Mantiene los estilos originales: gradiente azul, borde y sombra.
 *
 * - Sin `$inline`: posición `fixed` bottom-center del viewport (uso global desde App.tsx).
 * - Con `$inline`: posición `absolute` bottom-center relativa al contenedor
 *   (uso embebido en componentes como BusinessCarousel).
 */
export const ToastBadge = styled.div<{
  $inline?: boolean;
  $hasIcon?: boolean;
  $dismissible?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: ${({ $hasIcon }) => ($hasIcon ? "space-between" : "center")};
  gap: 10px;
  padding: ${({ $hasIcon }) => ($hasIcon ? "16px 10px 16px 10px" : "16px 24px")};
  overflow: ${({ $hasIcon }) => ($hasIcon ? "hidden" : "visible")};

  border-radius: 4px;
  border: 0.5px solid #fffdfd;
  background: linear-gradient(
      0deg,
      rgba(0, 0, 0, 0.2) 0%,
      rgba(0, 0, 0, 0.2) 100%
    ),
    linear-gradient(181deg, #437084 -0.02%, #76c7ea 100.02%);
  box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.3);

  left: 50%;
  transform: translateX(-50%);
  animation: ${fadeInUp} 0.25s ease-out;
  white-space: nowrap;
  pointer-events: ${({ $dismissible }) => ($dismissible ? "auto" : "none")};

  ${({ $inline }) =>
    $inline
      ? `
      position: absolute;
      bottom: 60px;
      z-index: 1;
    `
      : `
      position: fixed;
      bottom: calc(44px + 20px);
      z-index: ${tokens.zIndex.toast};
    `}

  @media (max-width: ${tokens.breakpoints.md - 1}px) {
    box-sizing: border-box;
    max-width: calc(100vw - 24px);
    justify-content: center;
    white-space: normal;
    text-align: center;
    overflow-wrap: anywhere;

    ${({ $hasIcon }) =>
      $hasIcon
        ? `
        flex-direction: column;
      `
        : ""}
  }
`;

export const ToastBadgeTitle = styled.span`
  margin: 0;
  max-width: 100%;
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 16px;
  color: ${tokens.colors.basics[0]};
`;

export const ToastCloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-left: 16px;
  padding: 4px;
  border: none;
  border-radius: 4px;
  background: transparent;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${tokens.colors.basics[0]};
    outline-offset: 2px;
  }
`;
